// ─────────────────────────────────────────────
//  FinishedRoomsCard — the rooms the clock says are done and the ROOM says are
//  still running, and the two things an AIC can do about each one.
//
//  Owner, 2026-09-09: a room whose last case ended more than the board's 15
//  minutes ago, with nothing to follow, clears itself and leaves the chart. For
//  the one case that cannot be decided on the clock — it ran long and nobody
//  updated the end time, so the room still reads `active` — he asked for
//  *"a warning in the right side of the time line and give an option to close
//  the room or keep it open"*. This is that warning.
//
//  ⚠️ THIS CARD ONLY EVER APPEARS FOR THE AMBIGUOUS ROOMS. A room that agrees it
//  is finished never reaches here — it is already gone from the chart. So an
//  empty card would be a card that says nothing on almost every day, and the
//  page renders nothing at all instead (see `rows.length === 0` below). That is
//  the opposite of the ORIENTEES card's rule, deliberately: an empty ORIENTEES
//  card is load-bearing because a trainee who is on the board and not in that
//  card is on no screen at all, whereas an empty version of THIS card asserts
//  nothing a reader needs.
//
//  ⚠️ THE TWO ACTIONS DO NOT SHARE A VERB. "Close the room" ends it and releases
//  everybody in it; "Keep it open" says the room is still running and stops the
//  board asking again. Naming them for the consequence rather than for the
//  control ("Yes" / "Dismiss") is the rule this repo already carries a scar
//  about: an operator reads the button, not the sentence above it.
// ─────────────────────────────────────────────
import React from "react";
import tokens from "../../theme/tokens";
import Card from "./PanelCard";

const { color, font, radius } = tokens;

// "1h 05m" / "45m". Same shape as `freeSince.elapsed`, from minutes rather than
// a pair of timestamps — this card is handed a duration the domain already
// worked out against the board's own clock, and asking the wall clock again
// here is how two parts of one screen come to disagree about the time.
export function sinceLabel(min) {
  if (!Number.isFinite(min)) return "";
  const whole = Math.max(0, Math.round(min));
  if (whole < 60) return `${whole}m`;
  return `${Math.floor(whole / 60)}h ${String(whole % 60).padStart(2, "0")}m`;
}

// ⚠️ THE TOKEN PATHS ARE THE RAIL'S OWN. `color.text.muted`, `color.border
// .default`, `color.surface.card`, `color.accent.primary` — nested, and a flat
// `color.text` reads `undefined`, which React writes into the DOM as no rule at
// all: the card renders, unstyled, and nothing throws.
const s = {
  intro: { fontSize: 11, color: color.text.muted, paddingBottom: 6, lineHeight: 1.35 },
  rows: { display: "flex", flexDirection: "column", gap: 7 },
  row: {
    display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap",
    padding: "8px 10px", border: `1px solid ${color.border.default}`, borderRadius: radius.sm,
  },
  // ⚠️ THE ROOM NAME IS WHAT THE ROW IS ABOUT, so it never shrinks; the meta
  // line beside it is the only thing that gives up width. Every previous row in
  // this app that let the name shrink ended up truncating the one word the
  // reader needed.
  name: { fontSize: 12.5, fontWeight: font.weight.bold, color: color.text.primary, whiteSpace: "nowrap", flexShrink: 0 },
  meta: { fontSize: 10.5, color: color.text.muted, flex: "1 1 auto", minWidth: 0, lineHeight: 1.35 },
  actions: { display: "flex", gap: 6, marginLeft: "auto", flexShrink: 0 },
  // Both are real buttons at the 24px WCAG 2.2 SC 2.5.8 floor, sized like the
  // rail's other row controls (`orienteeBtn`) so the panel keeps one language.
  btn: {
    minHeight: 24, minWidth: 24,
    display: "inline-flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box",
    fontSize: 10, fontWeight: 800, letterSpacing: ".5px", fontFamily: "inherit", lineHeight: 1,
    borderRadius: radius.sm, padding: "5px 9px", cursor: "pointer", whiteSpace: "nowrap",
    color: color.accent.primary, background: color.surface.card,
    border: `1px solid ${color.accent.primary}`,
  },
  // "Keep it open" is the conservative answer, so it is the quieter control —
  // but it is quieter in WEIGHT, never in size: both targets stay 24px.
  keep: { color: color.text.secondary, border: `1px solid ${color.border.default}` },
  busy: { cursor: "wait", opacity: 0.7 },
};

/**
 * @param rows    from `domain/roomRelease.roomReleaseRows().confirmRows`, each
 *                already carrying who is still attached to the room
 * @param onClose (roomId) => void — end the room and release everybody in it
 * @param onKeep  (roomId) => void — it really is still running; stop asking
 * @param busyId  the room a write is in flight for, or null
 */
export default function FinishedRoomsCard({ rows = [], onClose = null, onKeep = null, busyId = null }) {
  if (!rows.length) return null;
  return (
    <Card
      title="FINISHED ROOMS?"
      count={rows.length}
      note="past their last case, still marked running"
      testId="finished-rooms-card"
    >
      {/* ⚠️ THE SENTENCE SAYS WHY THE BOARD IS ASKING RATHER THAN ACTING. These
          rooms are exactly the ones where the clock and the room disagree, and
          an operator who does not know that cannot choose between the two
          buttons. */}
      <div style={s.intro}>
        Their last case is over, but the room still says a case is running. Close it only if
        it is genuinely empty.
      </div>
      <div style={s.rows}>
        {rows.map((r) => {
          const busy = busyId === r.roomId;
          const names = (r.people || []).map((p) => p.name).filter(Boolean);
          return (
            <div key={r.roomId} style={s.row}>
              <span style={s.name}>{r.roomName || "This room"}</span>
              <span style={s.meta}>
                {`last case ended ${sinceLabel(r.finishedForMin)} ago`}
                {/* Who the board lets go if this is closed. Named, because "and
                    3 others" is not something an AIC can check. */}
                {names.length > 0 && ` · ${names.join(", ")}`}
              </span>
              <span style={s.actions}>
                <button
                  type="button"
                  style={{ ...s.btn, ...(busy ? s.busy : null) }}
                  disabled={busy || !onClose}
                  aria-label={`Close ${r.roomName || "this room"} and release everybody in it`}
                  onClick={() => onClose && onClose(r.roomId)}
                >
                  {busy ? "CLOSING…" : "CLOSE THE ROOM"}
                </button>
                <button
                  type="button"
                  style={{ ...s.btn, ...s.keep, ...(busy ? s.busy : null) }}
                  disabled={busy || !onKeep}
                  aria-label={`Keep ${r.roomName || "this room"} open — it is still running`}
                  onClick={() => onKeep && onKeep(r.roomId)}
                >
                  KEEP IT OPEN
                </button>
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
